/**
 * useRealtimePushNotifications Hook
 * 
 * Automatically connects Supabase Realtime events to Browser Push Notifications.
 * When database changes occur (INSERT/UPDATE/DELETE), appropriate push notifications are triggered.
 * 
 * Usage:
 * ```tsx
 * function App() {
 *   useRealtimePushNotifications({ companyId: user.company_id });
 *   return <Dashboard />;
 * }
 * ```
 */

import { useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { RealtimeChannel } from '@supabase/supabase-js';
import { pushNotificationService } from '../services/pushNotifications.service';

interface UseRealtimePushNotificationsOptions {
  companyId?: string;
  enabled?: boolean;
  // Which tables to monitor
  tables?: {
    candidates?: boolean;
    tasks?: boolean;
    appointments?: boolean;
    activities?: boolean;
  };
}

const DEFAULT_COMPANY_ID = process.env.REACT_APP_DEFAULT_COMPANY_ID || '8cfea793-f739-475b-832c-53da0a702fd0';

export function useRealtimePushNotifications(options: UseRealtimePushNotificationsOptions = {}) {
  const {
    companyId = DEFAULT_COMPANY_ID,
    enabled = true,
    tables = {
      candidates: true,
      tasks: true,
      appointments: true,
      activities: true,
    },
  } = options;

  const channelRef = useRef<RealtimeChannel | null>(null);
  const errorCountRef = useRef<number>(0);
  const warnedMissingSupabaseSessionRef = useRef(false);
  const maxErrors = 3;

  useEffect(() => {
    if (!enabled || !companyId) {
      return;
    }

    let isCancelled = false;

    const initializeRealtimePush = async () => {
      errorCountRef.current = 0;

      // Request notification permission on mount
      pushNotificationService.requestPermission();

      // Realtime requires an authenticated Supabase session.
      // If app auth is backend-only, skip to avoid repeated CHANNEL_ERROR loops.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        if (!warnedMissingSupabaseSessionRef.current) {
          warnedMissingSupabaseSessionRef.current = true;
          console.warn('⚠️ [Realtime→Push] No Supabase session found - realtime push disabled.');
        }
        return;
      }

      if (isCancelled) return;

      // Create a single channel for all tables
      const channel = supabase.channel(`push-notifications-${companyId}`);

      // ========================
      // CANDIDATES
      // ========================
      if (tables.candidates) {
        channel.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'candidates',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const candidate = payload.new as any;
            const name = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Neuer Kandidat';
            const position = candidate.current_position || candidate.current_job_title || 'Position unbekannt';
            const matchScore = candidate.match_score;

            pushNotificationService.notifyNewCandidate(name, position, matchScore, candidate.id);
            console.log('🔔 [Realtime→Push] New candidate:', name);
          }
        );

        channel.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'candidates',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const oldCandidate = payload.old as any;
            const newCandidate = payload.new as any;

            // Only notify on status change
            if (oldCandidate.status !== newCandidate.status) {
              const name = `${newCandidate.first_name || ''} ${newCandidate.last_name || ''}`.trim() || 'Kandidat';
              pushNotificationService.notifyCandidateStatusChange(
                name,
                oldCandidate.status || 'Unbekannt',
                newCandidate.status || 'Unbekannt',
                newCandidate.id
              );
              console.log('🔔 [Realtime→Push] Candidate status changed:', name);
            }
          }
        );
      }

      // ========================
      // TASKS
      // ========================
      if (tables.tasks) {
        channel.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'tasks',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const task = payload.new as any;
            const title = task.title || 'Neue Aufgabe';
            const dueDate = task.due_date ? new Date(task.due_date).toLocaleDateString('de-DE') : undefined;

            pushNotificationService.notifyNewTask(title, undefined, task.id, dueDate);
            console.log('🔔 [Realtime→Push] New task:', title);
          }
        );

        channel.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'tasks',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const oldTask = payload.old as any;
            const newTask = payload.new as any;

            // Notify when task is completed
            if (!oldTask.completed && newTask.completed) {
              const title = newTask.title || 'Aufgabe';
              pushNotificationService.notifyTaskCompleted(title, 'Team', newTask.id);
              console.log('🔔 [Realtime→Push] Task completed:', title);
            }
          }
        );
      }

      // ========================
      // APPOINTMENTS
      // ========================
      if (tables.appointments) {
        channel.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'appointments',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const appointment = payload.new as any;
            const title = appointment.title || 'Neuer Termin';
            const date = appointment.date || '';
            const startTime = appointment.start_time?.substring(0, 5) || '';

            pushNotificationService.notifyNewAppointment(title, date, startTime, undefined, appointment.id);
            console.log('🔔 [Realtime→Push] New appointment:', title);
          }
        );

        channel.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'appointments',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const oldAppointment = payload.old as any;
            const newAppointment = payload.new as any;

            // Notify when appointment is cancelled
            if (oldAppointment.status !== 'cancelled' && newAppointment.status === 'cancelled') {
              const title = newAppointment.title || 'Termin';
              const date = newAppointment.date || '';
              pushNotificationService.notifyAppointmentCancelled(title, date, undefined, newAppointment.id);
              console.log('🔔 [Realtime→Push] Appointment cancelled:', title);
            }
          }
        );

        channel.on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'appointments',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const appointment = payload.old as any;
            const title = appointment.title || 'Termin';
            const date = appointment.date || '';

            pushNotificationService.notifyAppointmentCancelled(title, date, undefined, appointment.id);
            console.log('🔔 [Realtime→Push] Appointment deleted:', title);
          }
        );
      }

      // ========================
      // ACTIVITIES (Team Activity Feed)
      // ========================
      if (tables.activities) {
        channel.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'activities',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            const activity = payload.new as any;

            // Map activity type to action text
            const actionMap: Record<string, string> = {
              candidate_created: 'erstellt',
              candidate_status_changed: 'aktualisiert',
              task_created: 'erstellt',
              task_completed: 'abgeschlossen',
              appointment_created: 'erstellt',
              appointment_status_changed: 'aktualisiert',
              appointment_deleted: 'gelöscht',
            };

            const action = actionMap[activity.type] || activity.action || 'geändert';
            const entityType = activity.entity_type || 'Element';
            const entityName = activity.entity_name || 'Unbekannt';

            // Only notify for significant activities (not self-actions)
            // In a real app, you'd check if activity.user_id !== currentUser.id
            pushNotificationService.notifyActivity('Team-Mitglied', action, entityType, entityName);
            console.log('🔔 [Realtime→Push] Activity:', activity.type);
          }
        );
      }

      // Subscribe to the channel
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('🔔 [Realtime→Push] Connected successfully');
          errorCountRef.current = 0;
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          errorCountRef.current++;
          if (errorCountRef.current <= maxErrors) {
            console.warn(`🔔 [Realtime→Push] ${status} (${errorCountRef.current}/${maxErrors})`);
          } else if (errorCountRef.current === maxErrors + 1) {
            console.warn('🔔 [Realtime→Push] Max retries reached, disabling realtime push notifications.');
            if (channelRef.current) {
              supabase.removeChannel(channelRef.current);
              channelRef.current = null;
            }
          }
          // Silently ignore after max errors
        }
      });

      channelRef.current = channel;
    };

    initializeRealtimePush().catch((err) => {
      console.error('❌ [Realtime→Push] Failed to initialize realtime push channel:', err);
    });

    // Cleanup
    return () => {
      isCancelled = true;
      if (channelRef.current) {
        console.log('🔔 [Realtime→Push] Unsubscribing from push notifications channel');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [companyId, enabled, tables.candidates, tables.tasks, tables.appointments, tables.activities]);
}

export default useRealtimePushNotifications;
